import {
  useCallback, useEffect, useRef, useState,
} from 'react';
import { IconChevronDown, IconChevronUp } from '@tabler/icons-react';
import type { OptionType } from '../../types/forms.type';
import useOutsideClick from '../../hooks/useOutsideClick';

interface SimpleDropdownProps {
  id: string;
  options: OptionType[];
  selected: string;
  onChange: (value: string) => void;
  className?: string;
}

export default function SimpleDropdown({
  id, options, selected, onChange, className = '',
}: Readonly<SimpleDropdownProps>) {
  const [isOpen, setIsOpen] = useState(false);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const close = useCallback(() => {
    setIsOpen(false);
    triggerRef.current?.focus();
  }, []);

  const handleOutsideClick = () => {
    if (isOpen) {
      setIsOpen(false);
    }
  };
  const actionDropdownRef = useOutsideClick(handleOutsideClick);
  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && isOpen) close();
    };
    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, close]);

  const selectedLabel = options.find((dropdownOption) => dropdownOption.value === selected)?.label ?? selected;

  return (
    <div id={`dropdown-${id}-container`} ref={actionDropdownRef} className={`relative ${className}`}>
      <button
        ref={triggerRef}
        id={`${id}-trigger`}
        type="button"
        className="w-full text-left flex items-center justify-between
        gap-2 bg-white border border-pipgrey-border rounded-lg px-3 py-2 text-sm font-semibold text-piptext-default button-base"
        onClick={() => setIsOpen((prev) => !prev)}
        aria-haspopup="listbox"
        aria-expanded={isOpen}
        aria-controls={`${id}-listbox`}
      >
        <span>{selectedLabel}</span>
        {isOpen ? <IconChevronUp className="w-4 h-4 shrink-0" /> : <IconChevronDown className="w-4 h-4 shrink-0" />}
      </button>

      {isOpen && (
        <ul
          id={`${id}-listbox`}
          role="listbox"
          aria-labelledby={`${id}-trigger`}
          className="absolute z-10 left-0 top-full mt-1 w-full max-h-56 overflow-y-auto overscroll-contain bg-white border border-pipgrey-border rounded-lg shadow-sideShadow"
        >
          {options.map((option) => (
            <li key={option.value}>
              <button
                type="button"
                role="option"
                aria-selected={option.value === selected}
                className={`w-full text-left px-3 py-2 text-sm hover:bg-pipbg-subtle ${
                  option.value === selected
                    ? 'text-primary-dark font-semibold'
                    : 'text-secondary'
                }`}
                onClick={() => {
                  onChange(option.value);
                  close();
                }}
              >
                {option.label}
              </button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
